import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://passportvisaphoto.org';

function PrivacyNav() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-blue-950'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            className={`text-xl font-bold transition-colors duration-300 ${isScrolled ? 'text-gray-900' : 'text-white'}`}
          >
            Passport Visa Photo
          </Link>
          <Link
            to="/"
            className={`inline-flex items-center text-sm font-medium transition-colors duration-300 whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-blue-900' : 'text-white/80 hover:text-white'}`}
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>
      </div>
    </nav>
  );
}

function PrivacyFooter() {
  return (
    <footer className="bg-blue-950 text-white py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-blue-200">
            © 2026 Passport Visa Photo. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm text-blue-200">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">
              Home
            </Link>
            <a href="tel:+16476289141" className="hover:text-white transition-colors cursor-pointer">
              647-628-9141
            </a>
            <a href="mailto:info@passportvisaphoto.org" className="hover:text-white transition-colors cursor-pointer">
              info@passportvisaphoto.org
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy | Passport Visa Photo';

    const robotsMeta = document.createElement('meta');
    robotsMeta.name = 'robots';
    robotsMeta.content = 'noindex, nofollow';
    robotsMeta.id = 'privacy-robots';
    document.head.appendChild(robotsMeta);

    return () => {
      document.getElementById('privacy-robots')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PrivacyNav />

      {/* Page Header */}
      <header className="bg-blue-950 text-white pt-32 pb-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold">Privacy Policy</h1>
          <p className="text-blue-200 mt-2">Effective Date: January 1, 2026</p>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Welcome to Passport Visa Photo. We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using our website or services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We collect information that you provide directly to us when you:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Fill out our contact form on the website</li>
              <li>Call or email us to inquire about our services</li>
              <li>Visit our physical location for passport or visa photo services</li>
              <li>Subscribe to our newsletter or updates</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Personal Information:</strong> This may include your name, email address, phone number, and any other information you choose to provide in your communications with us.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Photographic Information:</strong> When you use our passport or visa photo services, we take photographs of you. These photos are provided to you for your official applications and are not stored by us beyond the time necessary to complete your service, unless you request otherwise.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We use the information we collect for the following purposes:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>To provide passport and visa photo services to you</li>
              <li>To respond to your inquiries and communicate with you</li>
              <li>To process appointments and service requests</li>
              <li>To send you service-related information and updates</li>
              <li>To improve our website and services</li>
              <li>To comply with legal obligations and government requirements</li>
              <li>To protect our rights and prevent fraud or abuse</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Sharing and Third Parties</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our website, conducting our business, or servicing you, provided they agree to keep this information confidential.</li>
              <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              We do not share your passport or visa photos with any third parties. These photos are provided exclusively to you for your personal use in official applications.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">Our website may use cookies and similar tracking technologies to enhance your browsing experience. Cookies are small data files stored on your device that help us:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Remember your preferences and settings</li>
              <li>Understand how you use our website</li>
              <li>Improve website functionality and performance</li>
              <li>Analyze website traffic and user behavior</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              You can control cookies through your browser settings. However, disabling cookies may affect your ability to use certain features of our website.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Passport and visa photos are typically deleted from our systems immediately after service completion, unless you specifically request us to retain copies for future use. Contact form submissions and email communications are retained for up to 2 years for customer service and record-keeping purposes.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">You have the following rights regarding your personal information:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li><strong>Access:</strong> You have the right to request access to the personal information we hold about you.</li>
              <li><strong>Correction:</strong> You have the right to request that we correct any inaccurate or incomplete information.</li>
              <li><strong>Deletion:</strong> You have the right to request that we delete your personal information, subject to certain legal exceptions.</li>
              <li><strong>Objection:</strong> You have the right to object to our processing of your personal information in certain circumstances.</li>
              <li><strong>Withdrawal of Consent:</strong> If we are processing your information based on your consent, you have the right to withdraw that consent at any time.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">To exercise any of these rights, please contact us using the information provided below.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our services are not directed to children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can delete such information.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="font-bold text-gray-900 mb-3">Passport Visa Photo</p>
              <div className="space-y-2 text-gray-700">
                <p className="flex items-start">
                  <i className="ri-map-pin-line text-lg mt-0.5 mr-3 flex-shrink-0 text-blue-900"></i>
                  <span>3351 Markham Rd Unit 110 (Red Building)<br />Scarborough, ON M1X 0A6</span>
                </p>
                <p className="flex items-center">
                  <i className="ri-phone-line text-lg mr-3 text-blue-900"></i>
                  <a href="tel:+16476289141" className="hover:text-blue-900 cursor-pointer">647-628-9141</a>
                </p>
                <p className="flex items-center">
                  <i className="ri-mail-line text-lg mr-3 text-blue-900"></i>
                  <a href="mailto:info@passportvisaphoto.org" className="hover:text-blue-900 cursor-pointer">info@passportvisaphoto.org</a>
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12 bg-blue-50 p-6 rounded-lg border-l-4 border-blue-900">
            <p className="text-gray-700 leading-relaxed">
              <strong>Acknowledgment:</strong> By using our website or services, you acknowledge that you have read and understood this Privacy Policy and agree to its terms.
            </p>
          </section>
        </div>

        <div className="text-center mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 bg-blue-950 text-white rounded-lg hover:bg-blue-900 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line mr-2"></i>
            Return to Home
          </Link>
        </div>
      </main>

      <PrivacyFooter />
    </div>
  );
}
